import { AuthProvider } from './utils/Auth';
import { OrganizationContextProvider } from './utils/OrganizationContext';
import { Routes, Route, BrowserRouter as Router, Outlet, Navigate } from 'react-router-dom';
import { AuthRedirect } from './utils/Auth/authRedirect.js';
import { NavigationContextProvider, NavigationConditional } from './utils/NavigationContext/index.js';
import ScrollToTop from './utils/hooks/useScrollToTop.js';
import AuthPage from './pages/AuthPage/index.js';
import Home from './pages/Home';
import QrCodes from './pages/QrCodes';
import Analytics from './pages/Analytics';
import MicrositeDisplay from './pages/MicrositeDisplay';
import Sonar from './pages/Sonar';
import SetUp from './pages/SetUp';
import FAQ from './pages/FAQ';
import Admin from './pages/Admin';
import Upgrade from './pages/Upgrade';
import Settings from './pages/Settings';
import PasswordResetRequest from './pages/PasswordResetRequest';
import Action from './pages/Action';
import AssetPage from './pages/AssetPage';
import TrackerPage from './pages/TrackerPage';
import SurveyPage from './pages/SurveyPage';
import NotFound from './pages/NotFound';
import FileDisplay from './pages/FileDisplay/index.js';
import Authenticate from './pages/Authenticate/index.js';
import SubQrInit from './pages/SubQrInit';
import AuthGuard from './components/AuthGuard/index.js';
import PermissionGate from './components/PermissionGate/index.js';
import QrEdit from './pages/QrEdit';
import QrDesign from './pages/QrDesign/index.js';
import QrDetails from './pages/QrDetails';
import QrLinked from './pages/QrLinked';
import QrConnectPage from './pages/QrConnectPage/index.js';
import ScheduleDisplay from './pages/ScheduleDisplay/index.js';
import Widgets from './pages/Widgets';
import OrganizationsDashboard from './pages/Organizations/Dashboard/index.js';
import CreateOrg from './pages/Organizations/CreateOrg/index.js';
import Members from './pages/Organizations/Members/index.js';
import InviteMember from './pages/Organizations/Members/Invite/index.js';
import OrganizationsSettings from './pages/Organizations/Settings/index.js';
import PosterPage from './pages/PosterPage/index.js';

function App() {
  return (
    <Router>
      <AuthProvider>
        <OrganizationContextProvider>
          <NavigationContextProvider >
            <NavigationConditional />
            <ScrollToTop />
            <Routes>
              <Route
                path='/'
                element={
                  <AuthGuard>
                    <Home />
                  </AuthGuard>
                }
            />
            <Route path='/auth' element={<AuthPage />} />
            <Route path='/signup' element={<AuthRedirect mode='signup' />} />
            <Route path='/login' element={<AuthRedirect mode='login' />} />
            <Route 
              path='/analytics' 
              element={
                <AuthGuard>
                  <PermissionGate permission='analytics' fallback={<Navigate to='/notfound' />} showLoading>
                    <Analytics />
                  </PermissionGate>
                </AuthGuard>
              } 
            />
            <Route
              path='/collection'
              element={
                <AuthGuard>
                  <Outlet />
                </AuthGuard>
              }
            >
              <Route index element={<QrCodes />} />
              <Route path='new' element={
                <PermissionGate permission='edit_qr' fallback={<Navigate to='/collection' />} showLoading>
                  <QrEdit reference='new' />
                </PermissionGate>
              } />
              <Route path=':id/edit' element={
                <PermissionGate permission='edit_qr' fallback={<Navigate to='/collection' />} showLoading>
                  <QrEdit reference='edit' />
                </PermissionGate>
              } />
              <Route path=':id/design' element={
                <PermissionGate permission='edit_qr' fallback={<Navigate to='/collection' />} showLoading>
                  <QrDesign reference='design' />
                </PermissionGate>
              } />
              <Route path=':id/details' element={<QrDetails reference='details' />} />
              <Route path=':id/linked' element={<QrLinked reference='linked' />} />
            </Route>
            <Route
              path='/organizations'
              element={
                <AuthGuard >
                  <Outlet />
                </AuthGuard>
              }
            >
              <Route index element={<OrganizationsDashboard />} />
              <Route path='create' element={<CreateOrg />} />
              <Route path='members' element={<Members />} />
              <Route path='members/invite' element={
                <PermissionGate permission='manage_users' fallback={<Navigate to='/organizations' />} showLoading>
                  <InviteMember />
                </PermissionGate>
              } />
              <Route path='settings' element={
                <PermissionGate permission='admin' fallback={<Navigate to='/organizations' />} showLoading>
                  <OrganizationsSettings />
                </PermissionGate>
              } />
            </Route>
            <Route path='/upgrade' element={<Upgrade />} />
            <Route path='/setup/:id' element={<SetUp />} />
            <Route
              path='/settings'
              element={
                <AuthGuard>
                  <Settings />
                </AuthGuard>
              }
            />
            <Route 
              path='/widgets' 
              element={
                <AuthGuard>
                  <Widgets />
                </AuthGuard>
              } 
            >
            </Route>
            <Route path='/faq' element={<FAQ />} />
            <Route path='/reset' element={<PasswordResetRequest />} />
            <Route path='/action' element={<Action />} />
            <Route path='/a/:id' element={<AssetPage />} />
            <Route path='/t/:id' element={<TrackerPage />} />
            <Route path='/s/:id' element={<SurveyPage />} />
            <Route path='/m/:id' element={<FileDisplay />} />
            <Route path='/p/:id' element={<FileDisplay />} />
            <Route path='/c/:id' element={<QrConnectPage />} />
            <Route path='/f/:id/:subId' element={<SubQrInit />} />
            <Route path='/r/:id/:scheduleId' element={<ScheduleDisplay />} />
			<Route path='/poster/:id' element={<PosterPage />} />
            <Route path='/site/:id' element={<MicrositeDisplay />} />
            <Route path='/authenticate' element={<Authenticate />} />
            <Route
              path='/home'
              element={
                <AuthGuard>
                  <Home />
                </AuthGuard>
              }
            />
            <Route path='/admin' element={
              <AuthGuard>
                <PermissionGate permission='admin' fallback={<Navigate to='/notfound' />} showLoading>
                  <Admin />
                </PermissionGate>
              </AuthGuard>
            }
            />
            <Route path='/notfound' element={<NotFound />} />
            <Route path='/sonar' element={<Sonar />} />
            <Route path='*' element={<NotFound />} />
          </Routes>
        </NavigationContextProvider>
      </OrganizationContextProvider>
    </AuthProvider>
    </Router>
  );
};

export default App;